import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { getMessages } from '../i18n';
const t = getMessages();
/**
 * localStorage key: whether the user has DISMISSED the disclosure. Same storage
 * discipline as the voice hint and the theme pref — blocked storage is non-fatal.
 *
 * STATE-FORM one-shot (spec §2.2 as landed 2026-08-17, docs!608 +
 * `decisions/product/2026-08-17-onboarding-oneshot-keying.md` option (a)): the
 * stored fact is a plain "seen", set on DISMISSAL and cleared on an OBSERVED
 * `unlinked`. The card fires when linkage is observed `linked` and the flag is
 * unset. Any non-null value means seen — which also migrates holders of the
 * previous value-keyed writes (the stored `linked_at` epoch string) to suppressed
 * rather than re-showing them a disclosure they already actioned.
 *
 * What this replaced, and why the replacement is the ruled shape: the prior
 * implementation keyed suppression off the `linked_at` VALUE (`seenFor !==
 * onboardingKey(linkedAt)`), which re-armed on every `linked_at` change — including
 * the unlink-of-earliest case, where the surviving link's later epoch re-fired the
 * card on an UNLINK, exactly the action that should never trigger it (the
 * user-visible bug the decision file's option table names). Under state-form that
 * re-arm path is gone: nothing compares timestamps, so nothing can mismatch.
 *
 * The retired sentinel, recorded because its docblock outlived it once already:
 * `LINKED_AT_UNKNOWN` existed to keep dismissal STICKING when a linked row arrived
 * without a `linked_at` — under value keying, keying off `undefined` compared
 * `undefined !== <stored>` forever, an undismissable card. Boolean keying has no
 * comparison, so the sentinel's failure mode is unreachable and the sentinel
 * retired with the mechanism it guarded. Do not re-add a value to this key: the
 * moment the stored payload carries meaning again, the mismatch class returns.
 */
export const ONBOARDING_SEEN_KEY = 'agnivo-onboarding-seen-for';
/**
 * Whether the disclosure is owed to this user right now.
 *
 * The trigger is "first web surface entry at which linkage is observed linked and
 * unseen" (m3b AMENDED 2026-08-14T10:20Z; state-form re-affirmed 2026-08-17). It is
 * deliberately NOT keyed to the `?link=<token>` OIDC callback: a Telegram-initiated
 * link completes inside Telegram and produces no web callback at all, so a
 * callback-keyed trigger would silently never fire for the main path. The callback
 * is one entry point among several, not the gate.
 *
 * Re-arm is OBSERVATION-gated: only a resolved `unlinked` clears the flag, so a
 * re-link the client never saw unlink does not re-fire (spec's v1 RETREAT — the
 * disclosure was delivered in the prior epoch), while a link/unlink/relink the
 * client DID watch re-fires once for the new epoch.
 *
 * Pure, so the closes-tab edge and the re-link edges are testable without a DOM.
 */
export function shouldShowOnboarding({ linkage, seen }) {
    return linkage === 'linked' && !seen;
}
/**
 * Two-thread v1 §2.2 — the one-time post-linkage disclosure.
 *
 * Copy is LOCKED SURFACE, verbatim from
 * `reports/product/2026-08-14-m3b-onboarding-card-copy.md`.
 *
 * This card exists to prevent a specific later surprise: the user who asks "why
 * doesn't it remember what I said on Telegram?" should have been told at the moment
 * their mental model changed from one conversation to two. That is why it is prose
 * and not a tooltip, and why the only way past it is an action that reads as
 * forward motion rather than as agreement.
 *
 * NOT modal (m3b): the user can scroll, navigate, and use the composer behind it.
 * The card is an announcement, so it carries `role="status"` rather than trapping
 * focus — a dialog role would promise a modality this deliberately does not have,
 * and would misreport the surface to a screen reader.
 */
export function OnboardingCard({ onDismiss }) {
    return (_jsxs("div", { "data-testid": "onboarding-card", role: "status", className: "mx-4 mt-3 flex shrink-0 flex-col gap-3 rounded-lg border border-border bg-surface p-4 text-sm text-ink", children: [_jsx("p", { className: "leading-relaxed", children: t.chat.onboarding.body }), _jsx("div", { className: "flex justify-end", children: _jsx("button", { type: "button", "data-testid": "onboarding-dismiss", onClick: onDismiss, className: "rounded-md bg-accent px-3 py-1.5 text-[13px] font-medium text-on-accent transition-opacity hover:opacity-90", children: t.chat.onboarding.continueToChat }) })] }));
}
//# sourceMappingURL=OnboardingCard.js.map